import gql from 'graphql-tag';

import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  LegacyTransferTableValidatedDestinationContainersQuery,
  ServerResolutionResult,
  StringResolutionType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {StorableInfo} from 'notebook/tables/typed-values/TypedValueTypes';

import {
  GridWithContainersQuery,
  GridWithContainersQueryVariables,
} from './__generated/GridWithContainersQuery';
import {postValidationGridContainersCustomEval} from './TransferTableContainersColumn';

type GridNode = ExtractByTypename<GridWithContainersQuery['storable'], 'BoxNode'>;

const GRID_WITH_CONTAINERS_QUERY = gql`
  query GridWithContainersQuery($gridId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    storable(id: $gridId) {
      ... on StorableInterface {
        id
        name
      }
      ... on GridInterface {
        cells {
          container {
            ... on ContainerNode {
              id
              name
              barcode {
                value
              }
            }
            ...InaccessibleInfoGraphQL
          }
          columnIndex
          rowIndex
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async getGridWithContainers(gridId: string): Promise<GridWithContainersQuery> {
    return await doQueryWithoutCache<GridWithContainersQuery, GridWithContainersQueryVariables>(
      {query: GRID_WITH_CONTAINERS_QUERY},
      {variables: {gridId}}
    );
  },
};

/**
 * Given the values for the parent storable, source container, num-containers, and position columns,
 * fetch the parent storable grid, validate the table row data, and generate the appropriate TypedValue to
 * persist in the Containers cell.
 *
 * @param query the custom resolution query defined in StringResolutionConstants.ts
 * @returns The appropriate TypedValue to persist for the Containers cell, or an error if validation fails.
 */
export async function fetchGridAndValidateContainers(
  query: LegacyTransferTableValidatedDestinationContainersQuery
): Promise<ServerResolutionResult> {
  const {
    containersTypedValue,
    parentStorableTypedValue,
    numContainersTypedValue,
    sourceContainerTypedValue,
    positions,
  } = query;

  const queryResult = await stubbableFunctions.getGridWithContainers(
    parentStorableTypedValue.storableSummaries[0].id
  );
  const gridObj = queryResult.storable;
  if (isInaccessible(gridObj)) {
    return {
      type: undefined,
      error: `Selected container is not available.`,
      resolvedValue: undefined,
    };
  }

  const grid = gridObj as GridNode;
  const placeholderContainers = positions.map(
    ({rowIndex, columnIndex}) =>
      grid.cells[rowIndex][columnIndex] && grid.cells[rowIndex][columnIndex].container
  );

  const existingContainersInParent: Array<StorableInfo | null> = placeholderContainers.map((container) =>
    container && !isInaccessible(container)
      ? {
          id: container.id,
          name: container.name,
          barcode: container.barcode ? container.barcode.value : null,
        }
      : null
  );

  const customEvalResult = postValidationGridContainersCustomEval(
    containersTypedValue,
    numContainersTypedValue,
    sourceContainerTypedValue,
    existingContainersInParent
  );
  if (customEvalResult.error) {
    return {
      type: undefined,
      error: customEvalResult.error.message,
      resolvedValue: undefined,
    };
  }

  return {
    type: StringResolutionType.CUSTOM,
    error: undefined,
    resolvedValue: {
      typedValue: customEvalResult.val,
    },
  };
}
